import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { CheckCircle, ChevronDown, ChevronRight, Loader, RefreshCw, Square, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatDate } from '@/lib/utils';
import {
  KIND_LABELS,
  fetchIntegrationRuns,
  fetchIntegrationStats,
  killIntegrationRun,
  type IntegrationRun,
  type IntegrationRunKind,
  type IntegrationRunStatus,
  type IntegrationStats,
} from '@/services/integrationRunApi';

const HEALTH_KINDS: IntegrationRunKind[] = [
  'TITULOS_ERP',
  'LOCACOES',
  'OFICINA_API',
  'OFICINA_CSV',
  'DISTRIBUICAO',
];

const KIND_COLORS: Record<string, string> = {
  TITULOS_ERP: '#2563eb',
  LOCACOES: '#059669',
  OFICINA_API: '#d97706',
  OFICINA_CSV: '#7c3aed',
  DISTRIBUICAO: '#db2777',
  CSV_TITULOS: '#64748b',
};

function statusIcon(status?: IntegrationRunStatus) {
  if (status === 'success') return <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />;
  if (status === 'error') return <XCircle className="h-3.5 w-3.5 text-red-600" />;
  if (status === 'running') return <Loader className="h-3.5 w-3.5 animate-spin text-blue-600" />;
  if (status === 'partial') return <CheckCircle className="h-3.5 w-3.5 text-amber-600" />;
  return <span className="h-3.5 w-3.5 rounded-full bg-muted-foreground/30" />;
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

export interface IntegrationOpsPanelProps {
  initialKindFilter?: IntegrationRunKind | '';
  onKindFilterConsumed?: () => void;
}

export const IntegrationOpsPanel: React.FC<IntegrationOpsPanelProps> = ({
  initialKindFilter,
  onKindFilterConsumed,
}) => {
  const [stats, setStats] = useState<IntegrationStats | null>(null);
  const [runs, setRuns] = useState<IntegrationRun[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [kindFilter, setKindFilter] = useState<IntegrationRunKind | ''>('');
  const [statusFilter, setStatusFilter] = useState<IntegrationRunStatus | ''>('');
  const [correlationId, setCorrelationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [killingId, setKillingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const historyRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!initialKindFilter) return;
    setKindFilter(initialKindFilter);
    setPage(1);
    onKindFilterConsumed?.();
    requestAnimationFrame(() => {
      historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [initialKindFilter, onKindFilterConsumed]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        fetchIntegrationStats(7),
        fetchIntegrationRuns({
          kind: kindFilter,
          status: statusFilter,
          correlationId: correlationId.trim() || undefined,
          page,
          limit: 20,
        }),
      ]);
      setStats(s);
      setRuns(list.items);
      setTotal(list.total);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [kindFilter, statusFilter, correlationId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleKill = async (run: IntegrationRun) => {
    if (run.status !== 'running') return;
    const label = KIND_LABELS[run.kind] ?? run.kind;
    if (
      !window.confirm(
        `Encerrar a execução ${label} iniciada em ${formatDate(new Date(run.startedAt), 'dd/MM/yyyy HH:mm')}? Isso libera a trava do tipo para novas execuções.`
      )
    ) {
      return;
    }
    setKillingId(run.id);
    try {
      await killIntegrationRun(run.id);
      toast.success(`Execução ${label} encerrada`);
      await load();
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Erro ao encerrar execução';
      toast.error(msg);
    } finally {
      setKillingId(null);
    }
  };

  const volumeChartData = useMemo(() => {
    if (!stats?.daily?.length) return [];
    const byDate = new Map<string, Record<string, number | string>>();
    for (const row of stats.daily) {
      const cur = byDate.get(row.date) ?? { date: row.date };
      cur[row.kind] = Number(cur[row.kind] ?? 0) + row.processed;
      byDate.set(row.date, cur);
    }
    return Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [stats]);

  const successChartData = useMemo(() => {
    if (!stats?.daily?.length) return [];
    const byDate = new Map<
      string,
      { date: string; runs: number; success: number; durationSum: number; durationCount: number }
    >();
    for (const row of stats.daily) {
      const cur = byDate.get(row.date) ?? {
        date: row.date,
        runs: 0,
        success: 0,
        durationSum: 0,
        durationCount: 0,
      };
      cur.runs += row.runs;
      cur.success += row.success;
      if (row.avgDurationMs != null) {
        cur.durationSum += row.avgDurationMs * row.runs;
        cur.durationCount += row.runs;
      }
      byDate.set(row.date, cur);
    }
    return Array.from(byDate.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        date: d.date,
        successRate: d.runs > 0 ? Math.round((d.success / d.runs) * 1000) / 10 : 0,
        avgDurationSec:
          d.durationCount > 0 ? Math.round(d.durationSum / d.durationCount / 100) / 10 : 0,
      }));
  }, [stats]);

  const operators = stats?.lastDistributionOperators ?? [];
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Operação das Integrações</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Saúde, histórico e volume dos últimos 7 dias.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {HEALTH_KINDS.map((kind) => {
              const last = stats?.lastByKind?.[kind];
              const active = kindFilter === kind;
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={() => {
                    setKindFilter((prev) => (prev === kind ? '' : kind));
                    setPage(1);
                    historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`rounded-lg border px-2.5 py-2 text-left transition ${
                    active
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                      : 'border-border/70 bg-muted/20 hover:bg-muted/40'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    {statusIcon(last?.status)}
                    <span className="text-[11px] font-semibold">{KIND_LABELS[kind]}</span>
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    {last?.startedAt
                      ? formatDate(new Date(last.startedAt), 'dd/MM HH:mm')
                      : 'Sem execuções'}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {formatDuration(last?.durationMs)}
                    {last?.processed != null ? ` · ${last.processed}` : ''}
                  </p>
                </button>
              );
            })}
          </div>

          {stats ? (
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="rounded-md border px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">Runs (7d)</p>
                <p className="text-lg font-semibold">{stats.totals.runs}</p>
              </div>
              <div className="rounded-md border px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">Taxa sucesso</p>
                <p className="text-lg font-semibold">{stats.totals.successRate}%</p>
              </div>
              <div className="rounded-md border px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">Processados</p>
                <p className="text-lg font-semibold">{stats.totals.processed.toLocaleString('pt-BR')}</p>
              </div>
              <div className="rounded-md border px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">p50 / p95</p>
                <p className="text-sm font-semibold">
                  {formatDuration(stats.duration.p50)} / {formatDuration(stats.duration.p95)}
                </p>
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="h-56 rounded-lg border p-2">
              <p className="mb-1 px-1 text-[11px] font-medium text-muted-foreground">
                Volume processado / dia
              </p>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={volumeChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {HEALTH_KINDS.map((kind) => (
                    <Bar
                      key={kind}
                      dataKey={kind}
                      name={KIND_LABELS[kind]}
                      stackId="a"
                      fill={KIND_COLORS[kind]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="h-56 rounded-lg border p-2">
              <p className="mb-1 px-1 text-[11px] font-medium text-muted-foreground">
                Taxa sucesso (%) e duração média (s)
              </p>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={successChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} domain={[0, 100]} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="successRate"
                    name="Sucesso %"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgDurationSec"
                    name="Duração s"
                    stroke="#d97706"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {operators.length > 0 ? (
            <div className="rounded-lg border p-3">
              <p className="mb-2 text-[11px] font-medium text-muted-foreground">
                Última distribuição — títulos por operador
              </p>
              <div className="space-y-1.5">
                {operators.map((op) => {
                  const max = Math.max(...operators.map((o) => o.titlesCount), 1);
                  const pct = Math.round((op.titlesCount / max) * 100);
                  return (
                    <div key={op.name} className="flex items-center gap-2 text-xs">
                      <span className="w-28 truncate text-muted-foreground" title={op.name}>
                        {op.name}
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
                        <div className="h-full rounded bg-pink-600/80" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-10 text-right font-medium">{op.titlesCount}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div ref={historyRef}>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Histórico unificado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <select
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={kindFilter}
                onChange={(e) => {
                  setKindFilter(e.target.value as IntegrationRunKind | '');
                  setPage(1);
                }}
                aria-label="Filtrar por tipo"
              >
                <option value="">Todos os tipos</option>
                {(Object.keys(KIND_LABELS) as IntegrationRunKind[]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABELS[k]}
                  </option>
                ))}
              </select>
              <select
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as IntegrationRunStatus | '');
                  setPage(1);
                }}
                aria-label="Filtrar por status"
              >
                <option value="">Todos os status</option>
                <option value="success">success</option>
                <option value="error">error</option>
                <option value="partial">partial</option>
                <option value="running">running</option>
              </select>
              <input
                className="h-8 min-w-[12rem] flex-1 rounded-md border border-input bg-background px-2 text-xs"
                placeholder="correlationId…"
                value={correlationId}
                onChange={(e) => setCorrelationId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1);
                    void load();
                  }
                }}
              />
            </div>

            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[640px] text-left text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2">Quando</th>
                    <th className="px-2 py-2">Tipo</th>
                    <th className="px-2 py-2">Trigger</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Proc./Err.</th>
                    <th className="px-2 py-2">Duração</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                        Nenhuma execução encontrada.
                      </td>
                    </tr>
                  ) : (
                    runs.map((run) => {
                      const open = expandedId === run.id;
                      return (
                        <React.Fragment key={run.id}>
                          <tr className="border-t border-border/50 hover:bg-muted/20">
                            <td className="whitespace-nowrap px-2 py-2">
                              {formatDate(new Date(run.startedAt), 'dd/MM/yyyy HH:mm:ss')}
                            </td>
                            <td className="px-2 py-2">{KIND_LABELS[run.kind] ?? run.kind}</td>
                            <td className="px-2 py-2">{run.trigger}</td>
                            <td className="px-2 py-2">
                              <span className="inline-flex items-center gap-1">
                                {statusIcon(run.status)}
                                {run.status}
                              </span>
                            </td>
                            <td className="px-2 py-2">
                              {run.processed ?? '—'}
                              {run.errors != null ? ` / ${run.errors}` : ''}
                              {run.distributed != null ? ` · dist ${run.distributed}` : ''}
                            </td>
                            <td className="px-2 py-2">{formatDuration(run.durationMs)}</td>
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-1">
                                {run.status === 'running' ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-[10px] text-red-700 border-red-300 hover:bg-red-50 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-950/40"
                                    disabled={killingId === run.id}
                                    onClick={() => void handleKill(run)}
                                    title="Encerrar execução (kill)"
                                  >
                                    {killingId === run.id ? (
                                      <Loader className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Square className="h-3 w-3 fill-current" />
                                    )}
                                    <span className="ml-1">Kill</span>
                                  </Button>
                                ) : null}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-1.5"
                                  onClick={() => setExpandedId(open ? null : run.id)}
                                >
                                  {open ? (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                                {run.correlationId ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-1.5 text-[10px]"
                                    onClick={() => {
                                      setCorrelationId(run.correlationId!);
                                      setPage(1);
                                    }}
                                    title="Ver correlacionados"
                                  >
                                    corr
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                          {open ? (
                            <tr className="border-t border-border/40 bg-muted/10">
                              <td colSpan={7} className="px-3 py-2">
                                <p className="text-[11px]">{run.message || '—'}</p>
                                {run.errorSummary ? (
                                  <p className="mt-1 whitespace-pre-wrap text-[11px] text-red-700 dark:text-red-300">
                                    {run.errorSummary}
                                  </p>
                                ) : null}
                                {run.details != null ? (
                                  <pre className="mt-2 max-h-40 overflow-auto rounded bg-background p-2 text-[10px] text-muted-foreground">
                                    {JSON.stringify(run.details, null, 2)}
                                  </pre>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {total} execução(ões) · página {page}/{totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
